import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  Apple,
  ArrowLeft,
  Check,
  Clipboard,
  Dumbbell,
  ExternalLink,
  Gift,
  Play,
  ShieldCheck,
} from 'lucide-react';
import './BeestmodeReferralLandingPage.css';

const REFERRAL_CODE_PATTERN = /^[A-Za-z0-9]{6,12}$/;
const TEMPLATE_SHARE_ID_PATTERN = /^[A-Za-z0-9_-]{16,96}$/;
const CANONICAL_INVITE_URL = 'https://hagidev-pages.web.app/invite';
const CANONICAL_TEMPLATE_SHARE_URL = 'https://hagidev-pages.web.app/template-share';
const APP_STORE_ID = '6745525936';
const APP_STORE_URL = 'https://apps.apple.com/il/app/beestmode/id6745525936';
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.hagidevstudios.beestmode';
const INVITE_PAGE_TITLE = 'הוזמנת ל-Beestmode | אימונים חזקים יותר';
const INVITE_PAGE_DESCRIPTION = 'הוזמנת ל-Beestmode — הורד את האפליקציה, הזן את קוד ההזמנה שלך והתאמן חזק יותר.';
const TEMPLATE_SHARE_PAGE_TITLE = 'מישהו שיתף איתך תוכנית אימון ב-Beestmode';
const TEMPLATE_SHARE_PAGE_DESCRIPTION = 'הורד את Beestmode כדי לפתוח תוכנית אימון ששיתפו איתך, לערוך אותה ולשמור אותה אצלך.';
// Embedded webviews (WhatsApp/Instagram/Facebook/etc.) silently block custom-scheme
// (beestmode://) navigation, so the app never opens. Detect them to guide the user to Safari.
const IN_APP_BROWSER_PATTERN = /(FBAN|FBAV|FB_IAB|Instagram|Messenger|WhatsApp|Line\/|Snapchat|Twitter|TikTok|MicroMessenger)/i;
// How long to wait for the app to take over before falling back to the App Store.
const APP_OPEN_FALLBACK_MS = 1600;

const detectInAppBrowser = () =>
  typeof navigator !== 'undefined' && IN_APP_BROWSER_PATTERN.test(navigator.userAgent || '');

const trackReferralEvent = (eventName, referralCode) => {
  const eventDetails = {
    referral_code: referralCode,
    source_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  };

  window.dispatchEvent(new CustomEvent(eventName, { detail: eventDetails }));

  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, eventDetails);
  }
};

const copyText = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand('copy');
  document.body.removeChild(textArea);
};

const ReferralShell = ({ children }) => (
  <main className="referral-page" dir="rtl">
    <div className="referral-page__ambient" aria-hidden="true" />
    <section className="referral-layout" aria-labelledby="referral-title">
      <article className="referral-card">
        <div className="referral-brand" aria-label="Beestmode">
          <img src="/Beestmode/logo_tilt.png" alt="" aria-hidden="true" />
          <span>BEESTMODE</span>
        </div>
        {children}
      </article>
    </section>
  </main>
);

const BeestmodeReferralLandingPage = () => {
  const { referralCode: pathCode, shareId: pathShareId } = useParams();
  const location = useLocation();
  const [copied, setCopied] = useState(false);
  const isTemplateSharePage = location.pathname.startsWith('/template-share');

  const referralCode = useMemo(() => {
    const queryCode = new URLSearchParams(location.search).get('code');
    return (pathCode || queryCode || '').trim();
  }, [location.search, pathCode]);

  const shareId = useMemo(() => {
    const queryShareId = new URLSearchParams(location.search).get('shareId');
    return (pathShareId || queryShareId || '').trim();
  }, [location.search, pathShareId]);

  const isValidCode = REFERRAL_CODE_PATTERN.test(referralCode);
  const isValidShareId = TEMPLATE_SHARE_ID_PATTERN.test(shareId);
  const inviteUrl = isValidCode
    ? `${CANONICAL_INVITE_URL}/${encodeURIComponent(referralCode)}`
    : CANONICAL_INVITE_URL;
  const templateShareUrl = isValidShareId
    ? `${CANONICAL_TEMPLATE_SHARE_URL}/${encodeURIComponent(shareId)}`
    : CANONICAL_TEMPLATE_SHARE_URL;
  const templateShareAppUrl = isValidShareId
    ? `beestmode://template-share/${encodeURIComponent(shareId)}`
    : templateShareUrl;
  const appArgumentUrl = isTemplateSharePage ? templateShareUrl : inviteUrl;

  useEffect(() => {
    if (isTemplateSharePage) {
      trackReferralEvent('workout_template_share_landing_viewed', isValidShareId ? shareId : undefined);
    } else {
      trackReferralEvent('referral_landing_viewed', isValidCode ? referralCode : undefined);
    }
  }, [isTemplateSharePage, isValidCode, isValidShareId, referralCode, shareId]);

  useEffect(() => {
    const previousTitle = document.title;
    const title = isTemplateSharePage ? TEMPLATE_SHARE_PAGE_TITLE : INVITE_PAGE_TITLE;
    const description = isTemplateSharePage ? TEMPLATE_SHARE_PAGE_DESCRIPTION : INVITE_PAGE_DESCRIPTION;
    const metadata = [
      ['meta[name="description"]', description],
      ['meta[property="og:title"]', title],
      ['meta[property="og:description"]', description],
      ['meta[name="twitter:title"]', title],
      ['meta[name="twitter:description"]', description],
    ].map(([selector, nextContent]) => {
      const element = document.head.querySelector(selector);
      return {
        element,
        nextContent,
        previousContent: element?.getAttribute('content'),
      };
    });

    document.title = title;
    metadata.forEach(({ element, nextContent }) => element?.setAttribute('content', nextContent));

    return () => {
      document.title = previousTitle;
      metadata.forEach(({ element, previousContent }) => element?.setAttribute('content', previousContent || ''));
    };
  }, [isTemplateSharePage]);

  useEffect(() => {
    const selector = 'meta[data-beestmode-referral-banner="true"]';
    const existingMeta = document.head.querySelector(selector);

    if ((!isTemplateSharePage && !isValidCode) || (isTemplateSharePage && !isValidShareId)) {
      existingMeta?.remove();
      return undefined;
    }

    const smartAppBanner = existingMeta || document.createElement('meta');
    smartAppBanner.name = 'apple-itunes-app';
    smartAppBanner.dataset.beestmodeReferralBanner = 'true';
    smartAppBanner.content = `app-id=${APP_STORE_ID}, app-argument=${appArgumentUrl}`;

    if (!existingMeta) {
      document.head.appendChild(smartAppBanner);
    }

    return () => {
      smartAppBanner.remove();
    };
  }, [appArgumentUrl, isTemplateSharePage, isValidCode, isValidShareId]);

  const handleCopy = async () => {
    try {
      await copyText(referralCode);
      setCopied(true);
      trackReferralEvent('referral_code_copied', referralCode);
      window.setTimeout(() => setCopied(false), 2200);
    } catch (error) {
      // The code remains selectable even if a browser denies clipboard access.
      console.error('Could not copy referral code', error);
    }
  };

  const handleAppStore = () => {
    trackReferralEvent('referral_app_store_tapped', referralCode);
  };

  const handleGooglePlay = () => {
    trackReferralEvent('referral_google_play_tapped', referralCode);
  };

  const isInAppBrowser = useMemo(() => detectInAppBrowser(), []);

  const handleOpenApp = () => {
    trackReferralEvent('workout_template_share_open_app_tapped', shareId);

    if (!isValidShareId) {
      return;
    }

    // If the app takes over, the page is backgrounded and becomes hidden. If that
    // hasn't happened by the timeout, the scheme was blocked or the app isn't
    // installed, so fall back to the App Store.
    let handedOffToApp = false;
    const markHandedOff = () => {
      handedOffToApp = true;
    };
    const onVisibilityChange = () => {
      if (document.hidden) {
        markHandedOff();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange, { once: true });
    window.addEventListener('pagehide', markHandedOff, { once: true });

    window.setTimeout(() => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', markHandedOff);
      if (!handedOffToApp && !document.hidden) {
        window.location.href = APP_STORE_URL;
      }
    }, APP_OPEN_FALLBACK_MS);

    window.location.href = templateShareAppUrl;
  };

  if (isTemplateSharePage) {
    return (
      <ReferralShell>
        {isValidShareId ? (
          <>
            <div className="referral-icon" aria-hidden="true">
              <Dumbbell size={30} strokeWidth={2.25} />
            </div>
            <p className="referral-eyebrow">תוכנית אימון משותפת</p>
            <h1 id="referral-title">קיבלת תוכנית אימון</h1>
            <p className="referral-intro">
              מישהו שיתף איתך תוכנית אימון. הורד את Beestmode כדי לפתוח אותה, לערוך אותה ולשמור אותה אצלך.
            </p>

            {isInAppBrowser && (
              <p className="referral-help in-app-browser-hint">
                אם הכפתור לא פותח את Beestmode, פתח את הקישור ב-Safari (תפריט ⋯ ← "פתח ב-Safari") ונסה שוב.
              </p>
            )}

            <div className="referral-actions template-share-actions">
              <a
                className="primary-action"
                href={templateShareAppUrl}
                onClick={(event) => {
                  event.preventDefault();
                  handleOpenApp();
                }}
                >
                  <ExternalLink size={18} />
                  <span>פתח ב-<bdi>Beestmode</bdi></span>
              </a>
              <a
                className="secondary-action"
                href={APP_STORE_URL}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackReferralEvent('workout_template_share_app_store_tapped', shareId)}
                >
                  <Apple size={19} />
                  <span>הורד מ-<bdi>App Store</bdi></span>
                <ArrowLeft className="action-arrow" size={17} />
              </a>
              <a
                className="secondary-action"
                href={GOOGLE_PLAY_URL}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackReferralEvent('workout_template_share_google_play_tapped', shareId)}
                >
                  <Play size={18} fill="currentColor" />
                  <span>הורד מ-<bdi>Google Play</bdi></span>
                <ArrowLeft className="action-arrow" size={17} />
              </a>
            </div>

            <p className="referral-help">
              אחרי ההתקנה, חזור לקישור הזה כדי לפתוח את התוכנית באפליקציה.
            </p>
          </>
        ) : (
          <div className="invalid-referral">
            <div className="referral-icon muted" aria-hidden="true">
              <ShieldCheck size={30} strokeWidth={2.25} />
            </div>
            <h1 id="referral-title">קישור התוכנית אינו תקין</h1>
            <p>בדוק את הקישור שקיבלת או בקש שישלחו לך את התוכנית מחדש.</p>
            <a className="secondary-action" href="/beestmode">
              לעזרה ותמיכה
            </a>
          </div>
        )}
      </ReferralShell>
    );
  }

  return (
    <ReferralShell>
      {isValidCode ? (
        <>
          <div className="referral-icon" aria-hidden="true">
            <Gift size={30} strokeWidth={2.25} />
          </div>
          <p className="referral-eyebrow">הזמנה אישית</p>
          <h1 id="referral-title">
            <span>הוזמנת ל-</span>
            <bdi>Beestmode</bdi>
          </h1>
          <p className="referral-intro">
            אחרי ההתקנה, הזן את קוד ההזמנה באפליקציה. כשתסיים את האימון הראשון,
            שניכם תוכלו לקבל 7 ימי פרימיום.
          </p>

          <section className="referral-code-panel" aria-label="קוד ההזמנה שלך">
            <span>קוד ההזמנה שלך</span>
            <output className="referral-code" aria-label={`קוד הזמנה ${referralCode}`}>{referralCode}</output>
            <button className="copy-button" type="button" onClick={handleCopy}>
              {copied ? <Check size={18} /> : <Clipboard size={18} />}
              {copied ? 'הקוד הועתק' : 'העתק קוד'}
            </button>
          </section>

          <div className="referral-actions">
            <a
              className="primary-action"
              href={APP_STORE_URL}
              target="_blank"
              rel="noreferrer"
              onClick={handleAppStore}
            >
              <Apple size={19} />
              <span>הורד מ-<bdi>App Store</bdi></span>
              <ArrowLeft className="action-arrow" size={17} />
            </a>
            <a
              className="secondary-action"
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noreferrer"
              onClick={handleGooglePlay}
            >
              <Play size={18} fill="currentColor" />
              <span>הורד מ-<bdi>Google Play</bdi></span>
              <ArrowLeft className="action-arrow" size={17} />
            </a>
          </div>

          <p className="referral-help">
            האפליקציה לא נפתחה? הורד אותה מ-App Store, חזור לכאן והעתק את הקוד כדי להזין אותו לאחר ההרשמה.
          </p>
        </>
      ) : (
        <div className="invalid-referral">
          <div className="referral-icon muted" aria-hidden="true">
            <ShieldCheck size={30} strokeWidth={2.25} />
          </div>
          <h1 id="referral-title">קוד ההזמנה אינו תקין</h1>
          <p>בדוק את הקישור שקיבלת או בקש מחברך לשלוח הזמנה חדשה.</p>
          <a className="secondary-action" href="/beestmode">
            לעזרה ותמיכה
          </a>
        </div>
      )}
    </ReferralShell>
  );
};

export default BeestmodeReferralLandingPage;
